import {
  BitkitIconComponent,
  IconAndroid,
  IconApple,
  IconCordova,
  IconFastlane,
  IconFlutter,
  IconGo,
  IconIonic,
  IconJava,
  IconKotlin,
  IconMacos,
  IconNodejs,
  IconOther,
  IconPython,
  IconReact,
  IconRuby,
} from '@bitrise/bitkit-v2';

export type ProjectType =
  | 'ios'
  | 'macos'
  | 'android'
  | 'cordova'
  | 'ionic'
  | 'react-native'
  | 'flutter'
  | 'kotlin-multiplatform'
  | 'fastlane'
  | 'node-js'
  | 'java'
  | 'ruby'
  | 'python'
  | 'go'
  | 'other';

const PROJECT_TYPE_OPTIONS: Array<{
  value: ProjectType;
  text: string;
  iconComponent: BitkitIconComponent;
}> = [
  { value: 'ios', text: 'iOS', iconComponent: IconApple },
  { value: 'macos', text: 'macOS', iconComponent: IconMacos },
  { value: 'android', text: 'Android', iconComponent: IconAndroid },
  { value: 'cordova', text: 'Cordova', iconComponent: IconCordova },
  { value: 'ionic', text: 'Ionic', iconComponent: IconIonic },
  { value: 'react-native', text: 'React Native', iconComponent: IconReact },
  { value: 'flutter', text: 'Flutter', iconComponent: IconFlutter },
  { value: 'kotlin-multiplatform', text: 'Kotlin Multiplatform', iconComponent: IconKotlin },
  { value: 'fastlane', text: 'Fastlane', iconComponent: IconFastlane },
  { value: 'node-js', text: 'Node.js', iconComponent: IconNodejs },
  { value: 'java', text: 'Java', iconComponent: IconJava },
  { value: 'ruby', text: 'Ruby', iconComponent: IconRuby },
  { value: 'python', text: 'Python', iconComponent: IconPython },
  { value: 'go', text: 'Go', iconComponent: IconGo },
  { value: 'other', text: 'Other', iconComponent: IconOther },
];

export const PROJECT_TYPE_ICON_COMPONENT_MAP: Record<ProjectType, BitkitIconComponent> = PROJECT_TYPE_OPTIONS.reduce(
  (acc, { value, iconComponent }) => {
    acc[value] = iconComponent;
    return acc;
  },
  {} as Record<ProjectType, BitkitIconComponent>,
);

export const PROJECT_TYPE_NAME_MAP: Record<ProjectType, string> = PROJECT_TYPE_OPTIONS.reduce(
  (acc, { value, text }) => {
    acc[value] = text;
    return acc;
  },
  {} as Record<ProjectType, string>,
);

// project_type reaches the UI as a free-form string (AI-generated configs, legacy values are not
// validated anywhere), so display lookups must tolerate values outside the ProjectType union.
const PROJECT_TYPE_VALUES = new Set<string>(PROJECT_TYPE_OPTIONS.map(({ value }) => value));

export const isProjectType = (type?: string | null): type is ProjectType => !!type && PROJECT_TYPE_VALUES.has(type);

export const getProjectTypeName = (type?: string | null): string =>
  isProjectType(type) ? PROJECT_TYPE_NAME_MAP[type] : PROJECT_TYPE_NAME_MAP.other;

export const getProjectTypeIcon = (type?: string | null): BitkitIconComponent =>
  isProjectType(type) ? PROJECT_TYPE_ICON_COMPONENT_MAP[type] : IconOther;
